import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Holding, updateHolding, computeTotals } from '../../services/holdingsService';
import { KeeperAssessment } from '../../types/geminiTypes';
import { runKeeperFlow, KeeperAnalysisMode } from '../../services/keeperService';
import { KeeperCard } from './KeeperCard';
import { StalenessPrompt } from './StalenessPrompt';
import { KeeperErrorBand, KeeperError, describeKeeperError } from './KeeperErrorBand';
import { LotManagerModal } from './LotManagerModal';
import { PortalMenu } from '../PortalMenu';
import { cn } from '../../lib/utils';
import { safeToFixed } from '../../lib/formatters';

interface HoldingRowProps {
  holding: Holding;
  uid: string;
  onRemove: (ticker: string, docId?: string) => void;
  onAnalyze: (ticker: string) => void;
  onKeeperComplete: (ticker: string, verdict: 'KEEP' | 'WATCH' | 'EXIT') => void;
  currentPrice?: number | null;
}

export function HoldingRow(props: HoldingRowProps) {
  const { holding, uid, onRemove, onAnalyze, onKeeperComplete } = props;
  const { totalShares, avgCostPerShare } = computeTotals(holding.lots ?? []);

  const [keeperLoading, setKeeperLoading] = useState(false);
  const [keeperProgress, setKeeperProgress] = useState('');
  const [keeperAssessment, setKeeperAssessment] = useState<KeeperAssessment | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [stalenessAge, setStalenessAge] = useState(0);
  const [showStalenessPrompt, setShowStalenessPrompt] = useState(false);
  const [keeperError, setKeeperError] = useState<KeeperError | null>(null);
  const [material8Ks, setMaterial8Ks] = useState<Array<{ filingDate: string; descriptions: string[] }>>([]);
  const [currentPrice, setCurrentPrice] = useState<number | null>(props.currentPrice ?? null);
  const [priceLoading, setPriceLoading] = useState<boolean>(props.currentPrice == null);
  const [priceError, setPriceError] = useState<boolean>(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssessMenu, setShowAssessMenu] = useState(false);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const assessMenuRef = useRef<HTMLDivElement>(null);
  const dotsMenuRef = useRef<HTMLDivElement>(null);

  const gainLoss =
    currentPrice !== null && avgCostPerShare > 0
      ? ((currentPrice - avgCostPerShare) / avgCostPerShare) * 100
      : null;

  const fetchPrice = useCallback(async () => {
    setPriceLoading(true);
    setPriceError(false);
    try {
      const res = await fetch(`/api/fmp/stable/quote?symbol=${holding.ticker}`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0 && data[0].price) {
          setCurrentPrice(data[0].price);
        }
      }
    } catch {
      console.error("Failed to fetch current price for", holding.ticker);
      setPriceError(true);
    } finally {
      setPriceLoading(false);
    }
  }, [holding.ticker]);

  useEffect(() => {
    if (!holding.lastOracleAnalysisDate) return;
    let cancelled = false;
    fetch(
      `/api/edgar/8k-check?ticker=${encodeURIComponent(holding.ticker)}&since=${encodeURIComponent(holding.lastOracleAnalysisDate)}`
    )
      .then(r => r.json())
      .then(data => {
        if (!cancelled && Array.isArray(data.filings)) {
          setMaterial8Ks(data.filings);
        }
      })
      .catch(() => {}); // Non-blocking — silently ignore failures
    return () => { cancelled = true; };
  }, [holding.ticker, holding.lastOracleAnalysisDate]);

  useEffect(() => {
    if (props.currentPrice === undefined) {
      setPriceLoading(true);
      setPriceError(false);
    } else if (props.currentPrice === null) {
      setPriceLoading(false);
      setPriceError(true);
      setCurrentPrice(null);
    } else {
      setCurrentPrice(props.currentPrice);
      setPriceLoading(false);
      setPriceError(false);
    }
  }, [props.currentPrice]);

  const handleAssessPosition = async (mode: KeeperAnalysisMode = 'auto') => {
    if (currentPrice === null) {
      console.error("Failed to fetch current price for", holding.ticker);
      setPriceError(true);
      return;
    }

    setShowStalenessPrompt(false);
    setKeeperError(null);
    setKeeperLoading(true);
    setKeeperProgress('Checking for existing analysis...');

    try {
      const result = await runKeeperFlow(
        { ticker: holding.ticker, currentPrice: currentPrice },
        uid,
        (msg) => setKeeperProgress(msg),
        mode
      );

      // 'auto' with a stale analysis returns no assessment — prompt the user.
      if (result.stalenessWarning && result.keeperAssessment === null) {
        setStalenessAge(result.analysisAgeDays ?? 0);
        setShowStalenessPrompt(true);
        setKeeperLoading(false);
        return;
      }

      setKeeperAssessment(result.keeperAssessment);
      if (result.keeperAssessment) {
        setIsExpanded(true);
        // Persist verdict to Firestore so badge updates
        await updateHolding(uid, holding.ticker, {
          lastKeeperVerdict: result.keeperAssessment.verdict,
          lastKeeperDate: new Date().toISOString(),
          lastAssessment: result.keeperAssessment
        });
        onKeeperComplete(holding.ticker, result.keeperAssessment.verdict);
      }
    } catch (err) {
      console.error('[KEEPER] Assessment failed:', err);
      setKeeperError(describeKeeperError(err));
    } finally {
      setKeeperLoading(false);
    } // Set timeout to clear error
  };

  const getBadgeColor = (status?: string) => {
    switch (status) {
      case 'KEEP': return 'bg-success-50 text-success-700 border border-success-100';
      case 'WATCH': return 'bg-warning-50 text-warning-700 border border-warning-100';
      case 'EXIT': return 'bg-danger-50 text-danger-700 border border-danger-100';
      default: return 'bg-surface-2 text-ink-600 border border-ink-200';
    }
  };

  const activeAssessment = keeperAssessment || holding.lastAssessment;

  return (
    <>
      <tr className="hover:bg-surface-2 transition-colors group">
        <td className="px-4 py-4 border-b border-ink-200 whitespace-nowrap">
          <div className="flex flex-col">
            <span className="font-mono font-medium text-sm text-ink">{holding.ticker}</span>
            <span className="text-xs text-ink-500 font-medium truncate max-w-[120px] sm:max-w-[160px]">{holding.companyName}</span>
            {material8Ks.length > 0 && (
              <span
                className="text-[9px] font-medium text-warning-700 uppercase tracking-wider mt-0.5 cursor-default"
                title={`Material SEC filing since last analysis: ${material8Ks.map(f => `${f.filingDate} — ${f.descriptions.join(', ')}`).join(' | ')}`}
              >
                ⚠ Material Filing
              </span>
            )}
          </div>
        </td>
        
        <td className="px-4 py-4 border-b border-ink-200 whitespace-nowrap text-right hidden sm:table-cell">
          <span className="text-sm text-ink font-mono">
            {totalShares > 0 ? totalShares.toLocaleString() : '—'}
          </span>
        </td>
        
        <td className="px-4 py-4 border-b border-ink-200 whitespace-nowrap text-right hidden sm:table-cell">
          <div className="flex flex-col items-end">
            <span className="text-sm text-ink font-mono">
              {avgCostPerShare > 0 ? `$${safeToFixed(avgCostPerShare, 2)}` : '—'}
            </span>
            <span className="text-[9px] text-ink-500 uppercase tracking-wider">per share</span>
          </div>
        </td>
        
        <td className="px-4 py-4 border-b border-ink-200 whitespace-nowrap text-right hidden md:table-cell">
          {priceLoading ? (
            <span className="text-sm text-ink font-mono font-medium">
              <Loader2 className="w-3 h-3 animate-spin text-ink-500 inline" />
            </span>
          ) : priceError ? (
            <span className="text-sm text-ink font-mono font-medium">
              <span className="text-xs text-danger-600 font-medium">Unavailable</span>
              <button
                onClick={() => fetchPrice()}
                className="text-[10px] text-danger-600 underline underline-offset-2 hover:text-danger-700 transition-colors ml-1"
              >
                Retry
              </button>
            </span>
          ) : currentPrice !== null ? (
            <div className="flex flex-col items-end">
              <span className="text-sm text-ink font-mono font-medium">
                ${safeToFixed(currentPrice, 2)}
              </span>
              {gainLoss !== null && (
                <span className={cn(
                  "text-[10px] font-medium",
                  gainLoss >= 0 ? "text-success-600" : "text-danger-600"
                )}>
                  {gainLoss >= 0 ? '+' : ''}{safeToFixed(gainLoss, 1)}%
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm text-ink font-mono font-medium">—</span>
          )}
        </td>
        
        <td className="px-4 py-4 border-b border-ink-200 whitespace-nowrap text-center">
          <div className="flex flex-col items-center gap-1">
            <button
               disabled={!activeAssessment}
               onClick={() => setIsExpanded(!isExpanded)}
               className={cn(
                 "inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider min-w-[70px] transition-colors",
                 getBadgeColor(holding.lastKeeperVerdict),
                 activeAssessment && "cursor-pointer hover:opacity-80 shadow-sm"
               )}
            >
              {holding.lastKeeperVerdict || 'Not Assessed'}
              {activeAssessment && (
                isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
              )}
            </button>
            {holding.lastKeeperDate && (
              <span className="text-[9px] text-ink-500 font-medium whitespace-nowrap">
                {new Date(holding.lastKeeperDate).toLocaleDateString()}
              </span>
            )}
          </div>
        </td>
        
        <td className="px-4 py-4 border-b border-ink-200 text-right">
          {keeperLoading ? (
            /* Loading state — full width spinner + progress text */
            <div className="flex items-center justify-end gap-2 text-ink-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span className="text-[10px] font-medium uppercase tracking-wider hidden lg:inline-block truncate max-w-[140px]">
                {keeperProgress}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2">

              {/* ── SPLIT ASSESS BUTTON ── */}
              <div className="relative" ref={assessMenuRef}>
                <div className="inline-flex rounded-md border border-ink-200">
                  {/* Left half — primary action */}
                  <button
                    onClick={() => { setShowAssessMenu(false); handleAssessPosition('auto'); }}
                    disabled={priceLoading || currentPrice === null || priceError}
                    className={cn(
                      "text-[11px] font-medium tracking-wide px-3 py-[5px]",
                      "rounded-l-md border-r border-ink-200 transition-colors",
                      "bg-surface text-ink hover:bg-surface-2",
                      (priceLoading || currentPrice === null || priceError) &&
                        "opacity-40 cursor-not-allowed hover:bg-surface"
                    )}
                    title={priceError ? "Price unavailable — cannot assess" : undefined}
                  >
                    Assess
                  </button>
                  {/* Right half — chevron dropdown trigger */}
                  <button
                    onClick={() => { setShowDotsMenu(false); setShowAssessMenu(v => !v); }}
                    disabled={priceLoading || currentPrice === null || priceError}
                    className={cn(
                      "px-[7px] py-[5px] rounded-r-md transition-colors flex items-center",
                      "bg-surface text-ink-500 hover:bg-surface-2 hover:text-ink-600",
                      (priceLoading || currentPrice === null || priceError) &&
                        "opacity-40 cursor-not-allowed hover:bg-surface"
                    )}
                    aria-label="Assess options"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </div>

                {/* Assess dropdown */}
                <PortalMenu
                  isOpen={showAssessMenu}
                  onClose={() => setShowAssessMenu(false)}
                  buttonRef={assessMenuRef}
                  className="z-50 bg-surface border border-ink-200 rounded-md py-1 min-w-[130px]"
                  style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                >
                  <button
                    onClick={() => { setShowAssessMenu(false); handleAssessPosition('fresh'); }}
                    className="w-full text-left px-3 py-[6px] text-[11px] font-medium tracking-wide text-ink hover:bg-surface-2 transition-colors"
                  >
                    Run Fresh
                  </button>
                  {activeAssessment && (
                    <button
                      onClick={() => { setShowAssessMenu(false); handleAssessPosition('stale'); }}
                      className="w-full text-left px-3 py-[6px] text-[11px] font-medium tracking-wide text-ink hover:bg-surface-2 transition-colors"
                    >
                      {stalenessAge > 0 ? `Use Old (${stalenessAge}d)` : 'Use Old'}
                    </button>
                  )}
                </PortalMenu>
              </div>

              {/* ── VERTICAL DOTS MENU ── */}
              <div className="relative" ref={dotsMenuRef}>
                <button
                  onClick={() => { setShowAssessMenu(false); setShowDotsMenu(v => !v); }}
                  className="w-[28px] h-[28px] flex items-center justify-center rounded-md border border-ink-200 text-ink-500 hover:bg-surface-2 hover:text-ink-600 transition-colors bg-surface"
                  aria-label="More actions"
                >
                  {/* Vertical dots — three stacked circles */}
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"
                       aria-hidden="true">
                    <circle cx="7" cy="2.5" r="1.2"/>
                    <circle cx="7" cy="7"   r="1.2"/>
                    <circle cx="7" cy="11.5" r="1.2"/>
                  </svg>
                </button>

                {/* Dots dropdown */}
                <PortalMenu
                  isOpen={showDotsMenu}
                  onClose={() => setShowDotsMenu(false)}
                  buttonRef={dotsMenuRef}
                  className="z-50 bg-surface border border-ink-200 rounded-md py-1 min-w-[140px]"
                  style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                >
                  {activeAssessment && (
                    <button
                      onClick={() => { setShowDotsMenu(false); setIsExpanded(!isExpanded); }}
                      className="w-full text-left px-3 py-[6px] text-[11px] font-medium tracking-wide text-ink hover:bg-surface-2 transition-colors flex items-center gap-1.5"
                    >
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {isExpanded ? 'Hide' : 'View'}
                    </button>
                  )}
                  <button
                    onClick={() => { setShowDotsMenu(false); onAnalyze(holding.ticker); }}
                    className="w-full text-left px-3 py-[6px] text-[11px] font-medium tracking-wide text-ink hover:bg-surface-2 transition-colors"
                  >
                    Analyze
                  </button>
                  <button
                    onClick={() => { setShowDotsMenu(false); setShowEditModal(true); }}
                    className="w-full text-left px-3 py-[6px] text-[11px] font-medium tracking-wide text-ink hover:bg-surface-2 transition-colors"
                  >
                    Lots
                  </button>
                  <div className="border-t border-ink-200 my-1" />
                  <button
                    onClick={() => { setShowDotsMenu(false); onRemove(holding.ticker, holding._docId); }}
                    className="w-full text-left px-3 py-[6px] text-[11px] font-medium tracking-wide text-danger-600 hover:bg-danger-50 transition-colors"
                  >
                    Remove
                  </button>
                </PortalMenu>
              </div>

            </div>
          )}
        </td>
      </tr>
      
      {showStalenessPrompt && (
        <tr>
          <td colSpan={6} className="p-0 border-b border-ink-200">
            <div className="px-4 py-3">
              <StalenessPrompt
                ageDays={stalenessAge}
                onRunFresh={() => { setShowStalenessPrompt(false); handleAssessPosition('fresh'); }}
                onUseOld={() => { setShowStalenessPrompt(false); handleAssessPosition('stale'); }}
              />
            </div>
          </td>
        </tr>
      )}

      {keeperError && !keeperLoading && (
        <tr>
          <td colSpan={6} className="p-0 border-b border-ink-200">
            <div className="px-4 py-3">
              <KeeperErrorBand
                error={keeperError}
                lastKeeperDate={holding.lastKeeperDate}
                hasPriorAssessment={!!activeAssessment}
              />
            </div>
          </td>
        </tr>
      )}

      <AnimatePresence>
      {isExpanded && activeAssessment && (
        <tr>
          <td colSpan={6} className="p-0 border-b border-ink-200">
            <KeeperCard
              assessment={activeAssessment} 
              onDismiss={() => setIsExpanded(false)} 
            />
          </td>
        </tr>
      )}
      </AnimatePresence>

      {showEditModal && (
        <LotManagerModal
          uid={uid}
          holding={holding}
          onClose={() => setShowEditModal(false)}
          onSaved={() => setShowEditModal(false)}
        />
      )}
    </>
  );
}
